/** @format */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Brand,
	Question,
	QuestionAssessmentObjectives,
	QuestionSpecification,
	type PaperAttachment,
	type QuestionNode,
	flattenTree,
	hasDisplayableContent,
} from "@question-bank/shared";
import { getQuestionChildren, getAssetUrl } from "../api/questions";
import { toStringArray, formatSeries, localisedContent } from "../utils/helpers";
import { buildPartsEntry, togglePart, type PaperEntry } from "../utils/paper";
import { ProxiedImage } from "./ProxiedImage";
import { QuestionContentBlocks } from "./QuestionContentBlocks";
import { QuestionPartTree } from "./QuestionPartTree";
import { useBrand } from "../hooks/useBrand";
import { usePaperById } from "../hooks/usePapersWithAttachments";
import { paperIdOf } from "../utils/attachments";
import { trackEvent } from "../utils/analytics";
import MarksIcon from "../assets/icons/check-square.svg?react";
import { toLangCode, type ExportLanguage } from "../utils/language";

interface QuestionModalProps {
	questionId: string;
	onClose?: () => void;
	onAddParts?: (entry: PaperEntry) => void;
	initialSelectedPartIds?: string[];
	preloadedItems?: Question[];
	isEmbedded?: boolean;
}

interface TabButtonProps {
	activeTab: TabKey;
	onTabChange: (tab: TabKey) => void;
	tabLabels: Record<TabKey, string>;
	tabKeys: readonly TabKey[];
}

/** Format a specification as a breadcrumb (theme › topic › subTopic). */
const formatSpecification = (specification?: QuestionSpecification): string =>
	specification ? [specification.theme, specification.topic, specification.subTopic].filter(Boolean).join(" › ") : "";

/** List the assessment objectives a question carries marks for (e.g. ["AO1", "AO2"]). */
const formatAssessmentObjectives = (ao?: QuestionAssessmentObjectives): string[] =>
	ao ? (["ao1", "ao2", "ao3"] as const).filter((key) => (ao[key] ?? 0) > 0).map((key) => key.toUpperCase()) : [];
const modalPillStyle =
	"font-bold not-italic text-[13px] inline-flex items-center py-0.5 px-2.5 rounded-[5px] gap-2.5 font-lato leading-normal text-center";

const TAB_WIDTHS: Record<TabKey, string> = {
	question: "min-w-[70px]",
	markScheme: "min-w-[110px]",
	examinersComments: "min-w-[160px]",
	additionalResources: "min-w-[160px]",
};
const getTabStyle = (isActive: boolean) =>
	isActive
		? "h-[34px] pt-2 pr-4 pb-2 pl-4 gap-1 text-[12px] text-(--public-color-btn-add) cursor-pointer"
		: "h-[34px] py-[6px] px-4 gap-1 text-[12px] cursor-pointer";

const TAB_KEYS = ["question", "markScheme", "examinersComments", "additionalResources"] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TabButtons = ({ activeTab, onTabChange, tabLabels, tabKeys }: TabButtonProps) => {
	const { t } = useTranslation();
	return (
		<div className="flex" role="tablist" aria-label={t("common.contentTabs")}>
			{tabKeys.map((key) => (
				<button
					type="button"
					key={key}
					role="tab"
					aria-selected={activeTab === key}
					onClick={() => onTabChange(key)}
					className={`${getTabStyle(activeTab === key)} ${TAB_WIDTHS[key]}`}
					style={{
						...(activeTab === key
							? {
									borderBottom: "2px solid var(--public-color-btn-add)",
									fontFamily: "Roboto, sans-serif",
									color: "var(--public-color-btn-add)",
									fontWeight: 400,
								}
							: { color: "var(--public-color-text-default)", fontWeight: 400 }),
					}}
				>
					{tabLabels[key]}
				</button>
			))}
		</div>
	);
};

interface TabContentProps {
	activeTab: TabKey;
	parts: Question[];
	tree: QuestionNode[];
	brand: Brand;
	activeLang: ExportLanguage;
	emptyLabels: Record<TabKey, string>;
	selectedPartIds: Set<string>;
	onTogglePart: (part: Question) => void;
	selectPartLabel: (questionNumber: string) => string;
	attachments: PaperAttachment[];
	resourcesEmptyLabel: string;
}

/** Shared heading shown above each part's content (AO badges, number, specification). */
const PartHeading = ({ part }: { part: Question }) => {
	const aos = formatAssessmentObjectives(part.assessmentObjectives);
	const specificationLabel = formatSpecification(part.specification);
	return (
		<div className="flex min-w-0 items-center gap-2.5">
			<span className="shrink-0 font-roboto text-[14px] font-bold text-(--public-color-text-filter)">
				{part.questionNumber}
			</span>
			{aos.map((ao) => (
				<span
					key={ao}
					className="shrink-0 flex h-5 items-center rounded-[5px] bg-(--public-color-ao-pill,#6ad1c9) px-2.5 font-lato text-[13px] font-bold"
				>
					{ao}
				</span>
			))}

			{specificationLabel && (
				<span
					className="min-w-0 flex-1 font-lato text-[12px] text-(--public-color-text-lighter) truncate mr-3"
					title={specificationLabel}
				>
					{specificationLabel}
				</span>
			)}
		</div>
	);
};

/** Lists a paper's attachments as clickable links that open each PDF in a new tab. */
const AdditionalResourcesTab = ({
	attachments,
	brand,
	emptyLabel,
}: {
	attachments: PaperAttachment[];
	brand: Brand;
	emptyLabel: string;
}) => {
	if (attachments.length === 0) {
		return (
			<div className="flex items-center justify-center h-full">
				<p className="text-(--public-color-text-lighter)">{emptyLabel}</p>
			</div>
		);
	}

	const languages: { key: "en" | "cy"; label: string }[] = [
		{ key: "en", label: "English" },
		{ key: "cy", label: "Cymraeg" },
	];

	return (
		<div className="flex flex-col gap-3 p-5">
			{attachments.map((attachment, index) => (
				<div
					key={attachment.id}
					className="flex flex-col gap-2 rounded-md border border-(--public-color-border-default) p-3"
				>
					<span className="font-roboto text-[14px] font-semibold text-(--public-color-text-filter)">
						{attachment.label || `Attachment ${index + 1}`}
					</span>
					<div className="flex flex-wrap gap-2">
						{languages
							.filter((language) => attachment[language.key])
							.map((language) => (
								<a
									key={language.key}
									href={getAssetUrl(brand, attachment[language.key] as string)}
									target="_blank"
									rel="noreferrer"
									className="flex items-center gap-1.5 rounded-md border border-(--public-color-theme-primary) px-3 py-1.5 font-roboto text-[13px] text-(--public-color-theme-primary) hover:bg-(--public-color-page-bg)"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
										strokeWidth={1.5}
										stroke="currentColor"
										className="size-4 shrink-0"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
										/>
									</svg>
									{language.label}
								</a>
							))}
					</div>
				</div>
			))}
		</div>
	);
};

const TabContent = ({
	activeTab,
	parts,
	tree,
	brand,
	activeLang,
	emptyLabels,
	selectedPartIds,
	onTogglePart,
	selectPartLabel,
	attachments,
	resourcesEmptyLabel,
}: TabContentProps) => {
	if (activeTab === "additionalResources") {
		return <AdditionalResourcesTab attachments={attachments} brand={brand} emptyLabel={resourcesEmptyLabel} />;
	}

	if (parts.length === 0) {
		return (
			<div className="flex items-center justify-center h-full">
				<p className="text-(--public-color-text-lighter)">{emptyLabels[activeTab]}</p>
			</div>
		);
	}

	if (activeTab === "question") {
		// The root question carries the shared stem/context for the group. It is
		// always included when adding, so it is pinned above the selectable parts
		// (no checkbox) rather than being listed as a selectable part itself.
		const rootPart = parts.find((part) => part.isRoot);
		return (
			<div className="flex flex-col gap-5 py-4 px-6">
				{rootPart && (
					<div className="flex w-full flex-col gap-3 bg-(--public-color-page-bg) rounded-[10px] p-4 border border-(--public-color-border-default)">
						<PartHeading part={rootPart} />
						<QuestionContentBlocks
							question={rootPart}
							language={toLangCode(activeLang)}
							brand={brand}
							containerClassName="flex flex-col gap-3"
							textClassName="whitespace-pre-wrap font-roboto text-[14px] leading-6 text-(--public-color-text-default)"
							imageClassName="w-full rounded-md object-contain"
							imageAlt={`Question ${rootPart.questionNumber}`}
						/>
					</div>
				)}
				<QuestionPartTree
					nodes={tree}
					brand={brand}
					activeLang={activeLang}
					selectedPartIds={selectedPartIds}
					onTogglePart={onTogglePart}
					selectPartLabel={selectPartLabel}
				/>
			</div>
		);
	}

	if (activeTab === "markScheme") {
		return (
			<div className="flex flex-col gap-5 py-4 px-6">
				{parts.map((part) => {
					const content = localisedContent(part, activeLang);
					return (
						<div
							key={part.id}
							className="flex flex-col gap-3 bg-(--public-color-page-bg) rounded-[10px] p-4 border border-(--public-color-border-default)"
						>
							<PartHeading part={part} />
							{content?.markSchemeImage ? (
								<ProxiedImage
									src={getAssetUrl(brand, content.markSchemeImage)}
									alt={`Mark scheme ${part.questionNumber}`}
									className="w-full rounded-md object-contain"
								/>
							) : (
								<p className="font-lato text-[13px] text-(--public-color-text-lighter)">{emptyLabels.markScheme}</p>
							)}
						</div>
					);
				})}
			</div>
		);
	}

	if (activeTab === "examinersComments") {
		return (
			<div className="flex flex-col gap-5 py-4 px-6">
				{parts.map((part) => {
					const content = localisedContent(part, activeLang);
					return (
						<div
							key={part.id}
							className="flex flex-col gap-3 bg-(--public-color-page-bg) rounded-[10px] p-4 border border-(--public-color-border-default)"
						>
							<PartHeading part={part} />
							{content?.examinersComments ? (
								<p className="whitespace-pre-wrap font-roboto text-[14px] leading-6 text-(--public-color-text-default)">
									{content.examinersComments}
								</p>
							) : (
								<p className="font-lato text-[13px] text-(--public-color-text-lighter)">
									{emptyLabels.examinersComments}
								</p>
							)}
						</div>
					);
				})}
			</div>
		);
	}

	return null;
};

export const QuestionModal = ({
	questionId,
	onClose,
	onAddParts,
	initialSelectedPartIds,
	preloadedItems,
	isEmbedded,
}: QuestionModalProps) => {
	const { t, i18n } = useTranslation();
	const { brand } = useBrand();

	const [activeLang, setActiveLang] = useState<ExportLanguage>(
		i18n.language?.toLowerCase().startsWith("cy") ? "cymraeg" : "english",
	);
	const [activeTab, setActiveTab] = useState<TabKey>("question");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [questionParts, setQuestionParts] = useState<Question[]>([]);
	const [questionTree, setQuestionTree] = useState<QuestionNode[]>([]);
	const [rootQuestion, setRootQuestion] = useState<Question | null>(null);
	const [selectedPartIds, setSelectedPartIds] = useState<Set<string>>(new Set());

	const handleTogglePart = (part: Question) => {
		setSelectedPartIds((current) => togglePart(questionParts, current, part));
	};

	const handleAddParts = () => {
		if (!rootQuestion || selectedPartIds.size === 0) {
			return;
		}

		if (!onAddParts) {
			return;
		}

		trackEvent("add_parts_to_paper", {
			question_id: rootQuestion.parentQuestionId ?? questionId,
			parts_count: selectedPartIds.size,
		});
		onAddParts(
			buildPartsEntry(
				rootQuestion,
				questionParts,
				selectedPartIds,
				paper ? { series: paper.series, subject: paper.subject } : undefined,
			),
		);
		onClose?.();
	};

	const getLangStyle = (lang: ExportLanguage) =>
		`cursor-pointer text-[12px] font-bold font-roboto p-1.75 ${
			activeLang === lang
				? "bg-(--public-color-btn-add) text-white w-full h-[25px] rounded-[4px] px-1.75 py-0.5"
				: "text-(--public-color-text-default)"
		}`;

	const LANG_OPTIONS = [
		{ lang: "english", label: "English" },
		{ lang: "cymraeg", label: "Cymraeg" },
	] as const;

	const tabLabels: Record<TabKey, string> = {
		question: t("common.question"),
		markScheme: t("common.markScheme"),
		examinersComments: t("common.examinersComments"),
		additionalResources: t("questionCard.additionalResources"),
	};
	const emptyLabels: Record<TabKey, string> = {
		question: t("questionsTable.noChildren"),
		markScheme: t("pdfExport.markingSchemeMissing"),
		examinersComments: t("pdfExport.examinerCommentsMissing"),
		additionalResources: t("questionCard.noAdditionalResources"),
	};

	// Header mirrors the smaller card: subject (Q: n), specification breadcrumb,
	// level/tier badges, minutes (1 per mark) and total marks. The paper-level
	// facts (subject, series, level, tier) are resolved from the owning Paper
	// (QB2-223), looked up by the question's paperId below.
	const headerQuestion = rootQuestion ?? questionParts[0] ?? null;
	const questionNumber = headerQuestion?.rootQuestionNumber ?? headerQuestion?.questionNumber ?? "";
	const specSource = headerQuestion?.specification ? headerQuestion : questionParts.find((part) => part.specification);
	const specificationLabel = formatSpecification(specSource?.specification);
	const marks = questionParts.reduce((sum, part) => sum + (part.maxMarks ?? 0), 0);
	const minutes = marks; // mins = marks currently
	const paperCode = headerQuestion?.paperCode;
	const paperId = headerQuestion ? paperIdOf(headerQuestion) : undefined;
	const paper = usePaperById(brand, paperId);
	const subjectLabel = toStringArray(paper?.subject).join(", ");
	const title = `${subjectLabel} (Q: ${questionNumber})`;
	const seriesLabel = formatSeries(paper?.series);
	const attachments = paper?.attachments ?? [];
	const hasResources = attachments.length > 0;
	const visibleTabKeys = TAB_KEYS.filter((key) => key !== "additionalResources" || hasResources);

	// Drop empty grouped parents so the modal only lists parts that actually
	// carry content or assets to display.
	const displayParts = questionParts.filter(hasDisplayableContent);

	const isAllSelected = displayParts.length > 0 && displayParts.every((part) => selectedPartIds.has(part.id));

	const handleSelectAll = () => {
		setSelectedPartIds(isAllSelected ? new Set() : new Set(displayParts.map((part) => part.id)));
	};

	useEffect(() => {
		const applyItems = (items: QuestionNode[]) => {
			setQuestionTree(items);
			const flattened = flattenTree(items);
			const seenIds = new Set<string>();
			const allParts = flattened.filter((part) => {
				if (seenIds.has(part.id)) {
					return false;
				}
				seenIds.add(part.id);
				return true;
			});
			setQuestionParts(allParts);
			const root = allParts.find((q) => q.isRoot) ?? null;
			setRootQuestion(root);
			const savedIds = initialSelectedPartIds ?? [];
			const defaultIds = savedIds.length > 0 ? savedIds : allParts.filter(hasDisplayableContent).map((part) => part.id);
			const ids = new Set(defaultIds);
			// The root carries the group's shared stem and is always included when
			// adding, so keep it selected regardless of the saved/default set.
			if (root && hasDisplayableContent(root)) {
				ids.add(root.id);
			}
			setSelectedPartIds(ids);
		};
		if (preloadedItems) {
			applyItems(preloadedItems as QuestionNode[]);
			return;
		}
		setIsLoading(true);
		setError(null);
		getQuestionChildren(brand, questionId)
			.then((response) => applyItems(response.items))
			.catch(() => setError("Unable to load question. Please try again"))
			.finally(() => setIsLoading(false));
	}, [brand, questionId, initialSelectedPartIds, preloadedItems]);

	if (error) {
		return <div>{error}</div>;
	}

	const wrapperPositionClassName = isEmbedded
		? "relative h-full mx-auto"
		: "fixed z-40 top-5 left-1/2 -translate-x-1/2 h-[95vh]";

	return (
		<div
			className={`flex flex-col ${wrapperPositionClassName} w-full max-w-176.25 bg-white rounded-lg border border-(--public-color-paper-modal-border) overflow-hidden`}
			data-testid="question-modal"
		>
			<div className="flex flex-col shrink-0 gap-2 border-b border-(--public-color-border-filter) p-4 bg-(--public-color-page-bg)">
				<div className="flex flex-row items-start justify-between w-full gap-4">
					<div className="flex flex-col min-w-0 gap-0.5">
						{headerQuestion ? (
							<>
								<p
									data-testid="question-modal-title"
									className="min-w-0 truncate capitalize font-roboto text-[20px] text-(--public-color-text-filter) align-middle"
								>
									{title}
								</p>
								{specificationLabel && (
									<span
										className="truncate font-lato text-[13px] text-(--public-color-text-lighter)"
										title={specificationLabel}
									>
										{specificationLabel}
									</span>
								)}
							</>
						) : (
							<>
								<span className="h-6 w-48 rounded bg-gray-200 animate-pulse" />
								<span className="h-4 w-32 rounded bg-gray-200 animate-pulse" />
							</>
						)}
					</div>
					<div className="flex shrink-0">
						<div className="flex shrink-0 h-7.75 w-31.25 mr-5 px-0.75 py-0.5 border border-(--public-color-border-default) items-center justify-between rounded-md bg-(--public-color-btn-lang-bg)">
							{LANG_OPTIONS.map(({ lang, label }) => (
								<button
									key={lang}
									type="button"
									aria-label={lang}
									onClick={() => {
										trackEvent("toggle_question_language", { language: lang });
										setActiveLang(lang);
									}}
									className={getLangStyle(lang)}
								>
									{label}
								</button>
							))}
						</div>

						<button type="button" aria-label="close paper modal" className="shrink-0 cursor-pointer" onClick={onClose}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={2}
								stroke="currentColor"
								className="size-6 opacity-50"
							>
								<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
							</svg>
						</button>
					</div>
				</div>
				<div className="flex h-5 items-center gap-4.25 text-[13px]">
					{paper?.qualification && (
						<span className={`${modalPillStyle} bg-(--public-gcse-theme)`}>
							{paper.qualification.title ?? paper.qualification.code}
						</span>
					)}
					{paper?.tier && (
						<span className={`${modalPillStyle} bg-(--public-difficulty-pill)`}>
							{paper.tier.title ?? paper.tier.code}
						</span>
					)}

					{paperCode && (
						<span className="flex items-center font-roboto text-[13px] text-(--public-color-text-lighter)">
							{t("paperModal.paper")} {paperCode}
						</span>
					)}

					{seriesLabel && (
						<span className="flex items-center font-roboto text-[13px] text-(--public-color-text-lighter)">
							{seriesLabel}
						</span>
					)}

					{hasResources && (
						<span className="flex items-center gap-1 font-roboto text-[13px] text-(--public-color-theme-primary)">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="size-4"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"
								/>
							</svg>
							{t("questionCard.additionalResources")}
						</span>
					)}

					{minutes > 0 && (
						<span className="flex items-center gap-1 font-roboto text-[13px] text-(--public-color-text-lighter)">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="size-4"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
								/>
							</svg>
							{minutes} {t("questionCard.minutes")}
						</span>
					)}

					{marks > 0 && (
						<span className="flex items-center gap-1 font-roboto text-[13px] text-(--public-color-text-lighter)">
							<MarksIcon className="size-3" />
							{marks} {t("paperModal.marks")}
						</span>
					)}
				</div>
			</div>

			<div
				role="tablist"
				className="flex flex-col md:flex-row shrink-0 md:items-center md:justify-between border-b border-(--public-color-border-default) bg-(--public-color-page-bg) pt-1.5 px-5 pb-0.5"
			>
				<div className="h-8.5">
					<TabButtons
						activeTab={activeTab}
						onTabChange={(tab) => {
							trackEvent("view_question_tab", { tab });
							setActiveTab(tab);
						}}
						tabLabels={tabLabels}
						tabKeys={visibleTabKeys}
					/>
				</div>
				<div className="flex justify-end md:block">
					<button
						data-testid="btn-select-all"
						type="button"
						onClick={handleSelectAll}
						className="flex items-center justify-center whitespace-nowrap cursor-pointer bg-(--public-color-theme-primary) text-white rounded-md h-7.75 min-w-19.75 py-1 px-2 border border-(--public-color-theme-primary) font-roboto text-[13px] gap-1.5"
					>
						{isAllSelected ? t("paperModal.unSelectAll") : t("paperModal.selectAll")}
					</button>
				</div>
			</div>
			<div key={activeTab} data-testid="modal-tab-content" className="flex-1 overflow-y-auto min-h-0">
				{isLoading ? (
					<div className="flex items-center justify-center h-full">
						<p> {t("common.loading")}...</p>
					</div>
				) : (
					<TabContent
						activeTab={activeTab}
						parts={displayParts}
						tree={questionTree}
						brand={brand}
						activeLang={activeLang}
						emptyLabels={emptyLabels}
						selectedPartIds={selectedPartIds}
						onTogglePart={handleTogglePart}
						selectPartLabel={(questionNumber) => t("paperModal.selectPart", { part: questionNumber })}
						attachments={attachments}
						resourcesEmptyLabel={emptyLabels.additionalResources}
					/>
				)}
			</div>

			<div className="flex flex-row gap-2 shrink-0 ml-auto h-15.75 p-3 w-full border-t border-(--public-color-border-filter) justify-end">
				<button
					type="button"
					data-testid="btn-close-modal"
					aria-label="close modal"
					className="flex items-center justify-center whitespace-nowrap min-w-16.75 h-9.5 py-1.5 px-3 gap-2 cursor-pointer text-(--public-color-btn-filters-bg) border border-(--public-color-btn-filters-bg) rounded-md bg-white text-[16px] font-roboto"
					onClick={onClose}
				>
					{t("common.close")}
				</button>

				<button
					data-testid="btn-add-parts"
					type="button"
					aria-label="add question"
					onClick={handleAddParts}
					disabled={selectedPartIds.size === 0}
					className="flex items-center justify-center whitespace-nowrap cursor-pointer bg-(--public-color-theme-primary) text-white rounded-md h-9.5 min-w-19.75 py-1 px-2 border border-(--public-color-theme-primary) font-roboto text-[16px] gap-1.5 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						strokeWidth={2}
						stroke="currentColor"
						className="size-4"
					>
						<path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
					</svg>

					{t("common.add")}
				</button>
			</div>
		</div>
	);
};
